import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, ArrowLeft } from "lucide-react";

export default function ProgramDayPage() {
  const { day } = useParams();
  const [data, setData] = useState(null);
  const [reflection, setReflection] = useState("");

  const load = async () => {
    const { data } = await api.get(`/program/day/${day}`);
    setData(data);
    setReflection(data.progress?.reflection || "");
  };
  useEffect(() => { load(); }, [day]);

  if (!data) return <div className="p-10 text-zinc-500">Loading…</div>;

  const p = data.progress || {};
  const toggle = async (k) => {
    const val = !p[k];
    await api.post("/program/progress", { day: parseInt(day), [k]: val });
    await load();
  };

  const saveReflection = async () => {
    await api.post("/program/progress", { day: parseInt(day), reflection, proof_done: reflection.trim().length > 0 });
    await api.post("/journal", { content: reflection, prompt: data.day.prompt, day: parseInt(day) });
    await load();
  };

  const commits = [
    { key: "move_done", label: "Move with intention" },
    { key: "goal_done", label: "Hit your movement goal" },
    { key: "fuel_done", label: "Fuel the rebuild" },
    { key: "proof_done", label: "Record the proof" },
  ];

  return (
    <div className="fade-up">
      <Link to="/program" className="btn-ghost inline-flex items-center gap-2 pl-0" data-testid="back-to-program">
        <ArrowLeft size={14}/> Back to 30
      </Link>
      <p className="eyebrow mt-4" style={{ color: "#FF0055" }}>Day {data.day.day} · {data.day.focus}</p>
      <h1 className="font-display font-black text-3xl md:text-5xl mt-2 brand-underline uppercase">
        {data.workout?.title}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-10">
        <div className="lg:col-span-2 space-y-4">
          <div className="card">
            <p className="eyebrow">Workout</p>
            <p className="text-sm text-zinc-400 mt-2">{data.workout?.focus} · {data.workout?.duration_min}m · {data.workout?.difficulty}</p>
            <div className="mt-5 divide-y divide-zinc-800">
              {data.workout?.exercises?.map((ex, i) => (
                <div key={i} className="py-3 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-display font-bold uppercase text-sm">{ex.name}</p>
                    <p className="text-xs text-zinc-500 mt-1">{ex.notes}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-display font-black text-lg leading-none">{ex.sets}×{ex.reps}</p>
                    <p className="eyebrow text-[9px] mt-1">Sets × Reps</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <p className="eyebrow">Record the proof</p>
            <p className="font-display font-black text-xl mt-2 uppercase leading-tight">{data.day.prompt}</p>
            <textarea
              className="input mt-4"
              rows={4}
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="Write your proof for today…"
              data-testid="day-reflection-input"
            />
            <button className="btn-primary mt-4" onClick={saveReflection} disabled={!reflection.trim()} data-testid="save-day-reflection">
              Save proof
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="card">
            <p className="eyebrow">4 commitments</p>
            <div className="mt-4 space-y-2">
              {commits.map((c) => (
                <button
                  key={c.key}
                  onClick={() => toggle(c.key)}
                  data-testid={`day-commit-${c.key}`}
                  className="w-full flex items-center gap-3 p-3 text-left transition-colors"
                  style={{
                    background: p[c.key] ? "rgba(255,0,85,0.08)" : "#1A1A1A",
                    border: `1px solid ${p[c.key] ? "#FF0055" : "#27272A"}`,
                  }}
                >
                  <div
                    className="w-6 h-6 flex items-center justify-center shrink-0"
                    style={{ background: p[c.key] ? "#FF0055" : "transparent", border: `1px solid ${p[c.key] ? "#FF0055" : "#3f3f46"}` }}
                  >
                    {p[c.key] && <Check size={14} strokeWidth={3} color="white"/>}
                  </div>
                  <span className="text-sm">{c.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <p className="eyebrow">Fuel focus</p>
            <p className="text-sm text-zinc-200 mt-2 leading-relaxed">{data.day.fuel_focus}</p>
          </div>
          <div className="card">
            <p className="eyebrow">Mindset</p>
            <p className="text-sm text-zinc-200 mt-2 leading-relaxed">{data.day.mindset}</p>
          </div>
          <div className="card">
            <p className="eyebrow">Recovery</p>
            <p className="text-sm text-zinc-200 mt-2 leading-relaxed">{data.day.recovery}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
